import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ArrowRight, Columns3, Loader2, Plus, Search, X } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { KbReviewerSelect } from "@/components/KbReviewerSelect";

const COLUMNS = [
  { key: "requests", title: "Requests", hint: "Gaps someone asked for" },
  { key: "drafts", title: "Drafts", hint: "Being written" },
  { key: "review", title: "In Review", hint: "Waiting on a reviewer" },
  { key: "published", title: "Published", hint: "Live in the KB" },
];

/** Article planner: the whole pipeline on one board, drag or use the arrows. */
export default function KBPlanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = usePersistedState("kb-planner.q", "");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState("");
  const [asking, setAsking] = useState(false);
  const [reviewFor, setReviewFor] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (skip) params.set("skip", String(skip));
      const { data: d } = await api.get(`/kb/planner?${params}`);
      setData(d);
    } catch {
      toast.error("Couldn't load the planner");
    } finally { setLoading(false); }
  }, [q, skip]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const move = async (slug, to, reviewer_id = "") => {
    try {
      await api.post("/kb/planner/move", { slug, to, reviewer_id });
      toast.success(to === "published" ? "Approved — it's live" : to === "review" ? "Submitted for review" : "Back to draft");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't move that article");
    }
  };

  // Only the assigned reviewer (never the person who submitted it) or a super
  // admin can take a card into Published.
  const canPublish = (card) => user?.role === "super_admin"
    || (!!card.reviewer_id && card.reviewer_id === user?.user_id && card.submitter_id !== user?.user_id);
  const askPublish = (card) => {
    if (!canPublish(card)) {
      toast.info(card.reviewer ? `Only ${card.reviewer} (the assigned reviewer) can approve this` : "Submit it for review first — publishing goes through the reviewer");
      return;
    }
    setConfirm({ card, to: "published" });
  };

  // Starting a draft from a request needs a reviewer (super admins may skip it).
  const startRequest = (req) => {
    if (user?.role === "super_admin" || req.article_slug) return doStart(req, "");
    setReviewFor({ ...req, name: req.title, forStart: true });
  };
  const doStart = async (req, reviewer_id) => {
    try {
      const { data: r } = await api.post(`/kb/requests/${req.req_id}/start`, { reviewer_id: reviewer_id || undefined });
      toast.success(r.existing ? "Draft already started" : "Draft created from the request");
      navigate(`/kb/a/${r.slug}/edit`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start that draft");
    }
  };

  const decline = async (req) => {
    try {
      await api.patch(`/kb/requests/${req.req_id}`, { status: "declined" });
      toast.success("Request declined");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update that request");
    }
  };

  const drop = (column) => {
    const item = dragging;
    setDragging(null);
    setOver("");
    if (!item || item.column === column) return;
    if (item.column === "requests") {
      if (column === "drafts") startRequest(item.req);
      else toast.info("A request has to become a draft first");
      return;
    }
    if (column === "requests") { toast.info("Articles can't move back into Requests"); return; }
    if (column === "review") { setReviewFor(item.card); return; }
    if (column === "published") { askPublish(item.card); return; }
    if (column === "drafts" && item.column === "published") {
      setConfirm({ card: item.card, to: "draft" });
      return;
    }
    move(item.card.slug, "draft");
  };

  const canEdit = data?.can_edit;

  return (
    <div className="mx-auto max-w-[1500px] space-y-4 p-6" data-testid="kb-planner-page">
      <Link to="/kb" className="inline-flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 dark:text-slate-400 dark:hover:text-slate-100">
        <ArrowLeft className="h-3.5 w-3.5" /> Knowledge Base
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-slate-100 dark:text-slate-50">
            <Columns3 className="h-6 w-6" /> Article planner
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Requests, drafts, reviews and published articles side by side. Drag a card between
            columns, or use the arrow on the card.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400 dark:text-slate-500" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }}
              className="w-56 pl-8" placeholder="Search titles" data-testid="kb-planner-search" />
          </div>
          <Button onClick={() => setAsking(true)} data-testid="kb-planner-request-btn">
            <Plus className="mr-1 h-4 w-4" /> Request an article
          </Button>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400 dark:text-slate-500" />}
        </div>
      </div>

      {asking && <RequestDialog onClose={() => setAsking(false)} onSaved={() => { setAsking(false); load(); }} />}
      <AttentionStrip data={data} onReload={load} onMove={(slug, to, card) => setConfirm({ card, to })} />
      {reviewFor && (
        <ReviewerDialog card={reviewFor} onClose={() => setReviewFor(null)}
          onPick={(id) => {
            if (reviewFor.forStart) doStart(reviewFor, id); else move(reviewFor.slug, "review", id);
            setReviewFor(null);
          }} />
      )}
      {confirm && (
        <Dialog open onOpenChange={(v) => { if (!v) setConfirm(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="kb-planner-confirm">
            <DialogHeader>
              <DialogTitle>{confirm.to === "published" ? (confirm.card.live ? "Approve this update?" : "Approve & publish this article?") : "Take it off the KB?"}</DialogTitle>
              <DialogDescription>
                {confirm.to === "published"
                  ? (confirm.card.live
                    ? `The reviewed version of "${confirm.card.name}" replaces what readers see now.`
                    : `"${confirm.card.name}" becomes visible to everyone its audience covers.`)
                  : `"${confirm.card.name}" goes back to draft and readers can no longer see it.`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirm(null)} data-testid="kb-planner-confirm-cancel">Cancel</Button>
              <Button data-testid="kb-planner-confirm-ok"
                variant={confirm.to === "published" ? "default" : "destructive"}
                onClick={() => { move(confirm.card.slug, confirm.to); setConfirm(null); }}>
                {confirm.to === "published" ? "Approve" : "Unpublish"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <div className="grid gap-3 lg:grid-cols-4">
        {COLUMNS.map((col) => (
          <div key={col.key} onDragOver={(e) => { e.preventDefault(); setOver(col.key); }}
            onDragLeave={() => setOver((o) => (o === col.key ? "" : o))}
            onDrop={(e) => { e.preventDefault(); drop(col.key); }}
            data-testid={`kb-planner-col-${col.key}`}
            className={`rounded-xl border p-2 transition-colors ${over === col.key
              ? "border-primary bg-primary/5"
              : "border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:border-slate-800 dark:bg-slate-900/40"}`}>
            <div className="flex items-baseline gap-2 px-1 pb-2">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">{col.title}</h2>
              <span className="rounded-full bg-slate-200 px-1.5 text-[11px] font-semibold text-slate-600 dark:text-slate-300 dark:bg-slate-800 dark:text-slate-300"
                data-testid={`kb-planner-count-${col.key}`}>
                {data?.counts?.[col.key] ?? "—"}
              </span>
            </div>
            <p className="px-1 pb-2 text-[11px] text-slate-400 dark:text-slate-500">{col.hint}</p>
            <div className="max-h-[62vh] space-y-2 overflow-y-auto pr-1">
              {col.key === "requests"
                ? (data?.requests || []).map((r) => (
                  <Card key={r.req_id} draggable={canEdit}
                    onDragStart={() => setDragging({ column: "requests", req: r })}
                    className="p-2.5" data-testid={`kb-planner-request-${r.req_id}`}>
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{r.title}</div>
                    {r.why && <p className="mt-1 line-clamp-3 text-xs text-slate-500 dark:text-slate-400">{r.why}</p>}
                    <div className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
                      {r.by_name} · {String(r.at || "").slice(0, 10)}
                      {r.category ? ` · ${r.category}` : ""}
                      {r.status === "in_progress" ? " · draft started" : ""}
                    </div>
                    {canEdit && (
                      <div className="mt-2 flex items-center gap-1">
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]"
                          onClick={() => startRequest(r)} data-testid={`kb-planner-start-${r.req_id}`}>
                          {r.article_slug ? "Open draft" : "Start draft"} <ArrowRight className="ml-1 h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-slate-500 dark:text-slate-400"
                          onClick={() => decline(r)} data-testid={`kb-planner-decline-${r.req_id}`}>
                          <X className="mr-1 h-3 w-3" /> Decline
                        </Button>
                      </div>
                    )}
                  </Card>
                ))
                : (data?.[col.key === "drafts" ? "drafts" : col.key] || []).map((c) => (
                  <Card key={c.slug} draggable={canEdit}
                    onDragStart={() => setDragging({ column: col.key, card: c })}
                    className="p-2.5" data-testid={`kb-planner-card-${c.slug}`}>
                    <Link to={`/kb/a/${c.slug}`} className="text-sm font-semibold text-slate-800 dark:text-slate-100 hover:underline dark:text-slate-100">
                      {c.name}
                    </Link>
                    <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500">
                      <span>
                        {c.category} · {String(c.updated_at || "").slice(0, 10)}
                        {c.reviewer ? ` · ${c.reviewer}` : ""}
                        {c.words ? ` · ${c.words} words` : ""}
                      </span>
                      {c.idle_days >= (data?.stale_days || 60) && (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
                          data-testid={`kb-planner-stale-${c.slug}`}>
                          untouched {c.idle_days} days
                        </span>
                      )}
                      {c.owner && (
                        <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 font-semibold text-slate-600 dark:text-slate-300 dark:bg-slate-800 dark:text-slate-300"
                          data-testid={`kb-planner-card-owner-${c.slug}`}>
                          {c.owner}
                        </span>
                      )}
                    </div>
                    {canEdit && (
                      <div className="mt-2 flex items-center gap-1">
                        <Link to={`/kb/a/${c.slug}/edit`}>
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]">Edit</Button>
                        </Link>
                        {col.key === "drafts" && (
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]"
                            onClick={() => setReviewFor(c)} data-testid={`kb-planner-toreview-${c.slug}`}>
                            To review <ArrowRight className="ml-1 h-3 w-3" />
                          </Button>
                        )}
                        {col.key === "review" && canPublish(c) && (
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]"
                            onClick={() => askPublish(c)}
                            data-testid={`kb-planner-publish-${c.slug}`}>
                            {c.live ? "Approve update" : "Approve & publish"} <ArrowRight className="ml-1 h-3 w-3" />
                          </Button>
                        )}
                        {col.key === "published" && (
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-slate-500 dark:text-slate-400"
                            onClick={() => setConfirm({ card: c, to: "draft" })}
                            data-testid={`kb-planner-unpublish-${c.slug}`}>
                            Unpublish
                          </Button>
                        )}
                      </div>
                    )}
                  </Card>
                ))}
              {col.key === "published" && (data?.counts?.published || 0) > (data?.published_page || 25) && (
                <div className="flex items-center justify-between gap-1 pt-1">
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" disabled={!skip}
                    onClick={() => setSkip(Math.max(0, skip - (data?.published_page || 25)))}
                    data-testid="kb-planner-pub-prev">Newer</Button>
                  <span className="text-[11px] text-slate-400 dark:text-slate-500">
                    {skip + 1}–{skip + (data?.published || []).length}
                  </span>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]"
                    onClick={() => setSkip(skip + (data?.published_page || 25))}
                    data-testid="kb-planner-pub-next">Older</Button>
                </div>
              )}
              {!loading && !(col.key === "requests" ? data?.requests : data?.[col.key])?.length && (
                <p className="px-1 py-6 text-center text-xs text-slate-400 dark:text-slate-500" data-testid={`kb-planner-empty-${col.key}`}>
                  Nothing here
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Rot that hides on a board of 1,457 cards: published articles whose titles
 *  still say they're unfinished, articles nobody has touched in a year, and
 *  drafts that have quietly stopped moving. */
function AttentionStrip({ data, onMove, onReload }) {
  const [open, setOpen] = useState("");
  const [assignee, setAssignee] = useState("");
  const [people, setPeople] = useState([]);
  const [assigning, setAssigning] = useState(false);
  useEffect(() => {
    api.get("/kb/planner/assignees").then(({ data: d }) => setPeople(d.assignees || []))
      .catch(() => setPeople([]));
  }, []);
  const assign = async (rows) => {
    if (!assignee || !rows.length) return;
    setAssigning(true);
    try {
      const { data: r } = await api.post("/kb/planner/assign-owner",
        { slugs: rows.map((c) => c.slug), user_id: assignee });
      toast.success(`${r.assigned} articles now owned by ${r.owner}`);
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't assign those");
    } finally { setAssigning(false); }
  };
  if (!data) return null;
  const groups = [
    { key: "unfinished_titles", label: "Titles still say unfinished", rows: data.attention?.unfinished_titles || [], published: true },
    { key: "rotten", label: `Untouched over ${Math.round((data.rotten_days || 365) / 365)} year`, rows: data.attention?.rotten || [], published: true },
    { key: "stale_drafts", label: `Drafts idle ${data.stale_days || 60}+ days`, rows: data.attention?.stale_drafts || [], published: false },
  ].filter((g) => (data.counts?.[g.key] || 0) > 0);
  if (!groups.length) return null;
  const shown = groups.find((g) => g.key === open);

  return (
    <Card className="border-amber-300/70 bg-amber-50/60 p-3 dark:border-amber-900 dark:bg-amber-950/20"
      data-testid="kb-planner-attention">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 dark:text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5" /> Needs attention
        </span>
        {groups.map((g) => (
          <button key={g.key} type="button" onClick={() => setOpen(open === g.key ? "" : g.key)}
            data-testid={`kb-planner-attention-${g.key}`}
            className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${open === g.key
              ? "border-amber-500 bg-white dark:bg-slate-900 text-amber-800 dark:bg-slate-900 dark:text-amber-300"
              : "border-amber-300/70 text-amber-800 hover:bg-white/70 dark:border-amber-900 dark:text-amber-300 dark:hover:bg-slate-900/60"}`}>
            {g.label} ({data.counts[g.key]})
          </button>
        ))}
      </div>
      {shown && data.can_edit && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-600 dark:text-slate-300">
          <span>Review owner for these {shown.rows.length}:</span>
          <Select value={assignee} onValueChange={setAssignee}>
            <SelectTrigger className="h-7 w-52 text-[11px]" data-testid="kb-planner-assignee-select">
              <SelectValue placeholder="Pick someone" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {people.map((p) => (
                <SelectItem key={p.user_id} value={p.user_id}>{p.name || p.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
            disabled={!assignee || assigning} onClick={() => assign(shown.rows)}
            data-testid="kb-planner-assign-btn">
            {assigning && <Loader2 className="mr-1 h-3 w-3 animate-spin" />} Assign
          </Button>
        </div>
      )}
      {shown && (
        <div className="mt-2 max-h-64 space-y-1 overflow-y-auto" data-testid={`kb-planner-attention-list-${shown.key}`}>
          {shown.rows.map((c) => (
            <div key={c.slug} className="flex flex-wrap items-center gap-2 rounded-lg bg-white dark:bg-slate-900 px-2 py-1.5 text-xs dark:bg-slate-900"
              data-testid={`kb-planner-attention-row-${c.slug}`}>
              <Link to={`/kb/a/${c.slug}`} className="font-medium text-slate-800 dark:text-slate-100 hover:underline dark:text-slate-100">
                {c.name}
              </Link>
              <span className="text-slate-400 dark:text-slate-500">{c.category}</span>
              <span className="ml-auto text-slate-400 dark:text-slate-500">
                {c.idle_days >= 0 ? `${c.idle_days} days idle` : "no date"}
              </span>
              {c.owner && (
                <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 dark:text-slate-300 dark:bg-slate-800 dark:text-slate-300"
                  data-testid={`kb-planner-owner-${c.slug}`}>
                  {c.owner}
                </span>
              )}
              <Link to={`/kb/a/${c.slug}/edit`}>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]">Edit</Button>
              </Link>
              {shown.published && data.can_edit && (
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-rose-600"
                  onClick={() => onMove(c.slug, "draft", c)}
                  data-testid={`kb-planner-attention-unpublish-${c.slug}`}>
                  Unpublish
                </Button>
              )}
            </div>
          ))}
          {shown.rows.length < (data.counts[shown.key] || 0) && (
            <p className="px-2 py-1 text-[11px] text-slate-400 dark:text-slate-500">
              Showing the {shown.rows.length} worst of {data.counts[shown.key]}.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

/** "We need an article on X" — open to anyone who can read the KB. */
export function RequestDialog({ onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [why, setWhy] = useState("");
  const [cats, setCats] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/kb/tree").then(({ data }) => {
      const nodes = Array.isArray(data) ? data : (data.tree || data.categories || []);
      setCats(nodes.map((n) => ({
        id: n.cat_id, name: `${"— ".repeat(Math.max(0, (n.depth || 1) - 1))}${n.name}`,
      })));
    }).catch(() => setCats([]));
  }, []);

  const save = async () => {
    if (!title.trim() || saving) return;
    setSaving(true);
    try {
      await api.post("/kb/requests", { title: title.trim(), why: why.trim(), category_id: categoryId });
      toast.success("Request sent to the KB editors");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send that request");
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="kb-request-dialog">
        <DialogHeader>
          <DialogTitle>Request an article</DialogTitle>
          <DialogDescription>
            Tell the KB editors what's missing — it lands on the planner board and they get a notification.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">What should it cover?</label>
            <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. How to move a DID between two customers"
              data-testid="kb-request-title" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Why it matters (optional)</label>
            <Textarea value={why} onChange={(e) => setWhy(e.target.value)} rows={4}
              placeholder="Who keeps asking, what goes wrong without it, any links or ticket numbers"
              data-testid="kb-request-why" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Where it belongs (optional)</label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger data-testid="kb-request-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="kb-request-cancel">Cancel</Button>
          <Button onClick={save} disabled={!title.trim() || saving} data-testid="kb-request-send">
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Moving a draft into review needs a reviewer — same workflow as the editor. */
function ReviewerDialog({ card, onClose, onPick }) {
  const [who, setWho] = useState(card.reviewer_id || "");
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="kb-planner-reviewer-dialog">
        <DialogHeader>
          <DialogTitle>Who should review it?</DialogTitle>
          <DialogDescription>{card.name}{card.forStart ? " — who reviews the new draft?" : " — you can't review your own work."}</DialogDescription>
        </DialogHeader>
        <KbReviewerSelect value={who} onChange={setWho} testid="kb-planner-reviewer-select" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="kb-planner-reviewer-cancel">Cancel</Button>
          <Button disabled={!who} onClick={() => onPick(who)} data-testid="kb-planner-reviewer-ok">
            {card.forStart ? "Start the draft" : "Submit for review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
